/**
 * Onboarding question definitions.
 */

// -- Types -----------------------------------------------------------

export type QuestionType =
  | "usage_type"
  | "select_goals"
  | "options"
  | "input"
  | "select"
  | "multiple_input"
  | "checkbox"
  | "first_action";

export type QuestionId =
  | "usageType"
  | "goal"
  | "meetingType"
  | "jobTitle"
  | "teamSize"
  | "companyName"
  | "sourceLanguage"
  | "inviteTeamMembers"
  | "syncCalendar"
  | "firstAction";

export interface QuestionOption {
  value: string;
  text?: string;
  icon?: string;
  description?: string;
  /** i18n key for display text (resolved via t() at render time) */
  textKey?: string;
  /** i18n key for description (resolved via t() at render time) */
  descriptionKey?: string;
  id?: string;
}

export interface OnboardingQuestion {
  id: QuestionId;
  type: QuestionType;
  question: string;
  /** i18n key for the question heading (resolved via t() at render time) */
  questionKey: string;
  description?: string;
  /** i18n key for the description (resolved via t() at render time) */
  descriptionKey?: string;
  options: QuestionOption[];
  isUserQuestion: boolean;
  isPersonal: boolean;
  excludeFromProgress: boolean;
  hasSkip: boolean;
  /**
   * Marks steps whose seeded answer is replayed into the submit payload when the step is not rendered
   * (see APOLLO_SHORTCIRCUITABLE_QUESTION_IDS).
   */
  skipIfApolloField?: "usageType" | "jobTitle" | "companyName" | "meetingType";
}

// -- Goal enum values (literal strings matching server) --------------

export const OnboardingGoals = {
  // Canonical 3-bucket goals (2026-08). The wizard renders only these three
  // plus "Others"; the legacy values below stay because existing users carry
  // them in users.onboarding.goal forever (server stores raw strings).
  SALES: "Sales",
  RESEARCH: "Research",
  MEETINGS: "Meetings",
  /** @deprecated legacy pre-2026-08 goal — kept for stored-value compatibility */
  INTERVIEW: "Interview or Focus Group Transcription & Analysis",
  SURVEY_RESPONSE: "Form or Survey Response Analysis",
  MEETING_TRANSCRIPTION: "Meeting Transcription & Summarization",
  PHONE_CALL: "Phone Call Transcription & Analysis",
  TRANSLATE_MEDIA: "Translate Media to Another Language",
  EMBED_RECORDER: "Capture Media With Embeddable Recorder",
  APIS: "Develop with Speak APIs",
} as const;

// Goals that show the meetingType step (Phone Call removed -- uses bare-goal mapping)
export const MEETING_GOALS = new Set<string>([
  OnboardingGoals.SALES,
  OnboardingGoals.RESEARCH,
  OnboardingGoals.MEETINGS,
  OnboardingGoals.INTERVIEW,
  OnboardingGoals.MEETING_TRANSCRIPTION,
]);

/**
 * Goals that skip the firstAction upload step — their first action is not
 * uploading a pre-existing file: Survey/Form (CSV import), Embed Recorder
 * (create a recorder), APIs (developer setup), and "Others" (ambiguous —
 * handled by the dashboard empty-state). Meeting Transcription is retained
 * for now because its first action (upload a recording vs. connect a calendar)
 * is genuinely ambiguous and needs its own decision.
 */
export const SKIP_FIRST_ACTION_GOALS = new Set<string>([
  OnboardingGoals.MEETINGS,
  OnboardingGoals.SURVEY_RESPONSE,
  OnboardingGoals.MEETING_TRANSCRIPTION,
  OnboardingGoals.TRANSLATE_MEDIA,
  OnboardingGoals.EMBED_RECORDER,
  OnboardingGoals.APIS,
  "Others (Specify)",
]);

/** Goals where meeting capture is the primary first action */
export const MEETING_LED_GOALS = new Set<string>([
  OnboardingGoals.MEETINGS,
  OnboardingGoals.MEETING_TRANSCRIPTION,
]);

// -- Option arrays ---------------------------------------------------

export const USAGE_TYPES: QuestionOption[] = [
  { value: "Work", icon: "briefcase", description: "For your company or team", textKey: "ONBOARDING.USAGE_TYPES.WORK.TEXT", descriptionKey: "ONBOARDING.USAGE_TYPES.WORK.DESCRIPTION" },
  { value: "Education", icon: "graduation-cap", description: "For school or research", textKey: "ONBOARDING.USAGE_TYPES.EDUCATION.TEXT", descriptionKey: "ONBOARDING.USAGE_TYPES.EDUCATION.DESCRIPTION" },
  { value: "Personal", icon: "user", description: "For personal projects", textKey: "ONBOARDING.USAGE_TYPES.PERSONAL.TEXT", descriptionKey: "ONBOARDING.USAGE_TYPES.PERSONAL.DESCRIPTION" },
];

export const GOALS: QuestionOption[] = [
  { id: "sales", value: OnboardingGoals.SALES, icon: "headphones", textKey: "ONBOARDING.GOALS.SALES", description: "Score calls, coach reps, and accelerate deals", descriptionKey: "ONBOARDING.GOALS.SALES_DESCRIPTION" },
  { id: "research", value: OnboardingGoals.RESEARCH, icon: "spell-check", textKey: "ONBOARDING.GOALS.RESEARCH", description: "Analyze interviews, focus groups, and surveys", descriptionKey: "ONBOARDING.GOALS.RESEARCH_DESCRIPTION" },
  { id: "meetings", value: OnboardingGoals.MEETINGS, icon: "bot", textKey: "ONBOARDING.GOALS.MEETINGS", description: "Capture, summarize, and act on every meeting", descriptionKey: "ONBOARDING.GOALS.MEETINGS_DESCRIPTION" },
  { id: "others", value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

// Per-goal meetingType options. Values MUST match server GOAL_FIELD_MAPPING keys exactly.
export const INTERVIEW_MEETING_TYPES: QuestionOption[] = [
  { value: "researchInterview", textKey: "ONBOARDING.MEETING_TYPES.RESEARCH_INTERVIEW" },
  { value: "focusGroup", textKey: "ONBOARDING.MEETING_TYPES.FOCUS_GROUP" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const MEETING_MEETING_TYPES: QuestionOption[] = [
  { value: "salesCall", textKey: "ONBOARDING.MEETING_TYPES.SALES" },
  { value: "coachingSession", textKey: "ONBOARDING.MEETING_TYPES.COACHING_SESSION" },
  { value: "rolePlay", textKey: "ONBOARDING.MEETING_TYPES.ROLE_PLAY" },
  { value: "teamMeetings", textKey: "ONBOARDING.MEETING_TYPES.INTERNAL" },
  { value: "researchMeeting", textKey: "ONBOARDING.MEETING_TYPES.RESEARCH" },
  { value: "supportCall", textKey: "ONBOARDING.MEETING_TYPES.SUPPORT" },
  { value: "externalMeeting", textKey: "ONBOARDING.MEETING_TYPES.EXTERNAL" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

/** Sales-bucket conversation types. Values MUST match server GOAL_FIELD_MAPPING keys. */
export const SALES_MEETING_TYPES: QuestionOption[] = [
  { value: "salesCall", textKey: "ONBOARDING.MEETING_TYPES.SALES" },
  { value: "coachingSession", textKey: "ONBOARDING.MEETING_TYPES.COACHING_SESSION" },
  { value: "rolePlay", textKey: "ONBOARDING.MEETING_TYPES.ROLE_PLAY" },
  { value: "supportCall", textKey: "ONBOARDING.MEETING_TYPES.SUPPORT" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

// Lookup map for handleGoalSelect
export const MEETING_TYPE_OPTIONS_BY_GOAL: Record<string, QuestionOption[]> = {
  [OnboardingGoals.SALES]: SALES_MEETING_TYPES,
  [OnboardingGoals.RESEARCH]: INTERVIEW_MEETING_TYPES,
  [OnboardingGoals.MEETINGS]: MEETING_MEETING_TYPES,
  [OnboardingGoals.INTERVIEW]: INTERVIEW_MEETING_TYPES,
  [OnboardingGoals.MEETING_TRANSCRIPTION]: MEETING_MEETING_TYPES,
};

/** Per-goal job titles: each bucket shows only its ICP-relevant roles. */
export const SALES_JOB_TITLES: QuestionOption[] = [
  { value: "Sales Leader", textKey: "ONBOARDING.JOB_TITLES.WORK.SALES_LEADER" },
  { value: "Sales Representative", textKey: "ONBOARDING.JOB_TITLES.WORK.SALES_REP" },
  { value: "Sales Trainer or Coach", textKey: "ONBOARDING.JOB_TITLES.WORK.SALES_TRAINER" },
  { value: "Customer Success or Support", textKey: "ONBOARDING.JOB_TITLES.WORK.CUSTOMER_SUCCESS" },
  { value: "Business Owner", textKey: "ONBOARDING.JOB_TITLES.WORK.BUSINESS_OWNER" },
  { value: "Executive", textKey: "ONBOARDING.JOB_TITLES.WORK.EXECUTIVE" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const RESEARCH_JOB_TITLES: QuestionOption[] = [
  { value: "Researcher", textKey: "ONBOARDING.JOB_TITLES.WORK.RESEARCHER" },
  { value: "Research / Insights Consultant", textKey: "ONBOARDING.JOB_TITLES.WORK.RESEARCH_INSIGHTS_CONSULTANT" },
  { value: "Consultant", textKey: "ONBOARDING.JOB_TITLES.WORK.CONSULTANT" },
  { value: "Product Manager", textKey: "ONBOARDING.JOB_TITLES.WORK.PRODUCT_MANAGER" },
  { value: "Marketer", textKey: "ONBOARDING.JOB_TITLES.WORK.MARKETER" },
  { value: "Executive", textKey: "ONBOARDING.JOB_TITLES.WORK.EXECUTIVE" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const MEETINGS_JOB_TITLES: QuestionOption[] = [
  { value: "Business Owner", textKey: "ONBOARDING.JOB_TITLES.WORK.BUSINESS_OWNER" },
  { value: "Executive", textKey: "ONBOARDING.JOB_TITLES.WORK.EXECUTIVE" },
  { value: "Product Manager", textKey: "ONBOARDING.JOB_TITLES.WORK.PRODUCT_MANAGER" },
  { value: "Consultant", textKey: "ONBOARDING.JOB_TITLES.WORK.CONSULTANT" },
  { value: "Customer Success or Support", textKey: "ONBOARDING.JOB_TITLES.WORK.CUSTOMER_SUCCESS" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const JOB_TITLE_OPTIONS_BY_GOAL: Record<string, QuestionOption[]> = {
  [OnboardingGoals.SALES]: SALES_JOB_TITLES,
  [OnboardingGoals.RESEARCH]: RESEARCH_JOB_TITLES,
  [OnboardingGoals.MEETINGS]: MEETINGS_JOB_TITLES,
};

export const WORK_JOB_TITLES: QuestionOption[] = [
  // Fallback for "Others": popular roles that do not belong to the Sales/Research/Meetings buckets.
  { value: "Business Owner", textKey: "ONBOARDING.JOB_TITLES.WORK.BUSINESS_OWNER" },
  { value: "Executive", textKey: "ONBOARDING.JOB_TITLES.WORK.EXECUTIVE" },
  { value: "Consultant", textKey: "ONBOARDING.JOB_TITLES.WORK.CONSULTANT" },
  { value: "Engineering or Development", textKey: "ONBOARDING.JOB_TITLES.WORK.ENGINEERING_DEVELOPMENT" },
  { value: "Product Manager", textKey: "ONBOARDING.JOB_TITLES.WORK.PRODUCT_MANAGER" },
  { value: "Marketer", textKey: "ONBOARDING.JOB_TITLES.WORK.MARKETER" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const EDUCATION_JOB_TITLES: QuestionOption[] = [
  { value: "Teacher", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.TEACHER" },
  { value: "Professor", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.PROFESSOR" },
  { value: "High School Student", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.HIGH_SCHOOL_STUDENT" },
  { value: "Undergraduate Student", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.UNDERGRADUATE_STUDENT" },
  { value: "Masters Student", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.MASTERS_STUDENT" },
  { value: "PhD Student", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.PHD_STUDENT" },
  { value: "Faculty Member", textKey: "ONBOARDING.JOB_TITLES.EDUCATION.FACULTY_MEMBER" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const PERSONAL_JOB_TITLES: QuestionOption[] = [
  { value: "High School or University Student", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.HIGH_SCHOOL_UNIVERSITY_STUDENT" },
  { value: "Masters or PhD Student", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.MASTERS_PHD_STUDENT" },
  { value: "Researcher", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.RESEARCHER" },
  { value: "Consultant", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.CONSULTANT" },
  { value: "Freelancer", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.FREELANCER" },
  { value: "Teacher", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.TEACHER" },
  { value: "Content Creator", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.CONTENT_CREATOR" },
  { value: "Sales Professional", textKey: "ONBOARDING.JOB_TITLES.PERSONAL.SALES_PROFESSIONAL" },
  { value: "Others (Specify)", textKey: "ONBOARDING.OTHERS" },
];

export const TEAM_SIZE_RANGES: QuestionOption[] = [
  { value: "1", text: "1" },
  { value: "2-5", text: "2 to 5" },
  { value: "6-10", text: "6 to 10" },
  { value: "11-25", text: "11 to 25" },
  { value: "26-50", text: "26 to 50" },
  { value: "51-100", text: "51 to 100" },
  { value: "101-250", text: "101 to 250" },
  { value: "250+", text: "250+" },
];

// -- Helpers ---------------------------------------------------------

/** Shuffle all elements except the last one (which is always "Others"). */
export function shuffleExceptLast<T>(arr: T[]): T[] {
  if (arr.length <= 1) return [...arr];
  const items = arr.slice(0, -1);
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return [...items, arr[arr.length - 1]];
}

// -- Question definitions (9 active steps) ---------------------------

export const ONBOARDING_QUESTIONS: OnboardingQuestion[] = [
  {
    id: "usageType",
    type: "usage_type",
    question: "How will you use Speak?",
    questionKey: "ONBOARDING.QUESTION.USAGE_TYPE",
    descriptionKey: "ONBOARDING.QUESTION.USAGE_TYPE_DESC",
    options: USAGE_TYPES,
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: false,
    hasSkip: false,
    skipIfApolloField: "usageType",
  },
  {
    id: "goal",
    type: "select_goals",
    question: "What is your primary goal?",
    questionKey: "ONBOARDING.QUESTION.GOAL",
    descriptionKey: "ONBOARDING.QUESTION.GOAL_DESC",
    options: GOALS,
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: false,
    hasSkip: false,
  },
  {
    id: "meetingType",
    type: "options",
    question: "What type of meetings do you have?",
    questionKey: "ONBOARDING.QUESTION.MEETING_TYPE",
    descriptionKey: "ONBOARDING.QUESTION.MEETING_TYPE_DESC",
    options: MEETING_MEETING_TYPES, // Default; overridden per-goal in handleGoalSelect
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: false,
    hasSkip: false,
    skipIfApolloField: "meetingType",
  },
  {
    id: "jobTitle",
    type: "options",
    question: "What is your job title?",
    questionKey: "ONBOARDING.QUESTION.JOB_TITLE",
    descriptionKey: "ONBOARDING.QUESTION.JOB_TITLE_DESC",
    options: WORK_JOB_TITLES,
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: false,
    hasSkip: false,
    skipIfApolloField: "jobTitle",
  },
  {
    id: "companyName",
    type: "input",
    question: "What is your company name?",
    questionKey: "ONBOARDING.QUESTION.COMPANY_NAME",
    descriptionKey: "ONBOARDING.QUESTION.COMPANY_NAME_DESC",
    options: [],
    isUserQuestion: false,
    isPersonal: false,
    excludeFromProgress: false,
    hasSkip: false,
    skipIfApolloField: "companyName",
  },
  {
    id: "sourceLanguage",
    type: "select",
    question: "What language is your content in?",
    questionKey: "ONBOARDING.QUESTION.LANGUAGE",
    descriptionKey: "ONBOARDING.QUESTION.LANGUAGE_DESC",
    options: [],
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: false,
    hasSkip: false,
  },
  {
    id: "inviteTeamMembers",
    type: "multiple_input",
    question: "Invite your team members",
    questionKey: "ONBOARDING.QUESTION.INVITE",
    descriptionKey: "ONBOARDING.QUESTION.INVITE_DESC",
    options: [],
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: true,
    hasSkip: true,
  },
  {
    id: "syncCalendar",
    type: "checkbox",
    question: "Sync your calendar",
    questionKey: "ONBOARDING.QUESTION.CONNECT_CALENDAR",
    description: "Automatically transcribe and analyze your meetings",
    descriptionKey: "ONBOARDING.QUESTION.CALENDAR_DESC",
    options: [],
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: true,
    hasSkip: true,
  },
  {
    id: "firstAction",
    type: "first_action",
    question: "Take your first action",
    questionKey: "ONBOARDING.QUESTION.FIRST_ACTION",
    descriptionKey: "ONBOARDING.QUESTION.FIRST_ACTION_DESC",
    options: [],
    isUserQuestion: true,
    isPersonal: true,
    excludeFromProgress: true,
    hasSkip: true,
  },
];

/**
 * IDs of steps whose seeded answer is replayed into the submit payload when the step is not
 * rendered. Derived from the `skipIfApolloField` annotations above so the replay path in
 * handleComplete cannot drift from them.
 */
export const APOLLO_SHORTCIRCUITABLE_QUESTION_IDS: QuestionId[] = ONBOARDING_QUESTIONS
  .filter((q) => q.skipIfApolloField)
  .map((q) => q.id);
